/**
 * RangeQuestion Component
 * Handles range slider inputs for numerical values
 */

import { useState, useEffect } from 'react'

interface RangeQuestionProps {
  questionId: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  required?: boolean
  error?: string
  unit?: string // e.g., "kWh", "£", "%"
}

const RangeQuestion = ({
  questionId,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  required = false,
  error,
  unit = ''
}: RangeQuestionProps) => {
  // Local state to allow free editing (including empty values)
  const [inputValue, setInputValue] = useState<string>(String(value))

  // Sync local state when external value changes (e.g., from slider)
  useEffect(() => {
    setInputValue(String(value))
  }, [value])

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value)
    onChange(newValue)
    setInputValue(String(newValue))
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow free editing - don't enforce constraints while typing
    setInputValue(e.target.value)
  }

  const handleInputBlur = () => {
    // Validate and clamp on blur
    const parsed = inputValue === '' ? min : Number(inputValue)
    const clampedValue = Math.min(Math.max(parsed, min), max)
    onChange(clampedValue)
    setInputValue(String(clampedValue))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Apply value on Enter key
    if (e.key === 'Enter') {
      handleInputBlur()
    }
  }

  // Calculate percentage for styling
  const percentage = ((value - min) / (max - min)) * 100

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="mb-6">
        {/* Current Value Display - Editable */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-baseline gap-2 bg-primary-green bg-opacity-10 px-8 py-4 rounded-lg">
            <input
              type="number"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              min={min}
              max={max}
              step={step}
              className="w-20 text-5xl font-bold text-primary-green bg-transparent border-none text-center focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-opacity-50 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              aria-label="Enter value"
            />
            {unit && (
              <span className="text-2xl font-medium text-primary-green">
                {unit}
              </span>
            )}
          </div>
        </div>

        {/* Range Slider */}
        <div className="relative">
          <input
            type="range"
            id={questionId}
            name={questionId}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={handleRangeChange}
            required={required}
            autoFocus
            className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #10b981 0%, #10b981 ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`
            }}
            aria-invalid={!!error}
            aria-describedby={error ? `${questionId}-error` : undefined}
          />

          {/* Min/Max Labels */}
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>
              {min}
              {unit}
            </span>
            <span>
              {max}
              {unit}
            </span>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          id={`${questionId}-error`}
          className="mt-2 text-sm text-red-600 flex items-center gap-1"
          role="alert"
        >
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          transition: all 0.2s;
        }

        .slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
        }

        .slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          transition: all 0.2s;
        }

        .slider::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
        }

        .slider:focus {
          outline: none;
        }

        .slider:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.2);
        }

        .slider:focus::-moz-range-thumb {
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.2);
        }
      `}</style>
    </div>
  )
}

export default RangeQuestion
